import { Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { ApiResponseError } from '../../../types/errors';
import { useDeleteTheme } from '../hooks/use-themes';
import type { Theme } from '../types/theme';

export function DeleteThemeModal({ theme, onClose }: { theme: Theme | null; onClose: () => void }) {
  const deleteTheme = useDeleteTheme();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (theme) {
      setError(null);
    }
  }, [theme]);

  async function handleDelete() {
    if (!theme) {
      return;
    }
    setError(null);
    try {
      await deleteTheme.mutateAsync(theme.id);
      onClose();
    } catch (err) {
      if (err instanceof ApiResponseError && err.status === 404) {
        setError(
          t('features:themes.pages.themes_list_page.modals.delete.not_found', { name: theme.name }),
        );
        return;
      }
      setError(
        t('features:themes.pages.themes_list_page.modals.delete.failed', { name: theme.name }),
      );
    }
  }

  return (
    <Modal
      isOpen={theme !== null}
      onClose={onClose}
      primaryAction={{
        text: t('buttons:delete'),
        danger: true,
        onClick: () => void handleDelete(),
        disabled: deleteTheme.isPending,
        loading: deleteTheme.isPending,
      }}
      secondaryAction={{
        text: t('buttons:cancel'),
        onClick: onClose,
      }}
    >
      <Modal.Header>{t('features:themes.pages.themes_list_page.modals.delete.title')}</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-2">
          <Text>
            {t('features:themes.pages.themes_list_page.modals.delete.message', {
              name: theme?.name ?? '',
            })}
          </Text>
          {error && <Text variant="secondary">{error}</Text>}
        </div>
      </Modal.Body>
    </Modal>
  );
}
